// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState } from 'react'
import { ChevronDown, ChevronUp, Info, Lightbulb, AlertTriangle, Heart } from 'lucide-react'
import { cn } from '@/lib/utils'

interface InfoBlockProps {
  title:    string
  children: React.ReactNode
  defaultOpen?: boolean
  variant?: 'info' | 'warning' | 'neutral' | 'blue' | 'emotional'
}

// Per variant: container (achtergrond + rand), accent-linkerrand, icoon-chip en icoon.
// Eén consistente visuele hiërarchie: rustige accentkleur, duidelijke titel, ruime witruimte.
const STYLES: Record<NonNullable<InfoBlockProps['variant']>, {
  container: string; accent: string; chip: string; Icon: typeof Info
}> = {
  info:      { container: 'bg-sage-50 border-sage-200',                accent: 'border-l-sage-500',        chip: 'bg-sage-700/10 text-sage-700',               Icon: Lightbulb },
  warning:   { container: 'bg-status-warnBg border-status-warnBorder', accent: 'border-l-status-warnText', chip: 'bg-status-warnText/10 text-status-warnText', Icon: AlertTriangle },
  neutral:   { container: 'bg-sand border-sand-300',                   accent: 'border-l-sage-400',        chip: 'bg-ink-3/10 text-ink-3',                     Icon: Info },
  blue:      { container: 'bg-status-infoBg border-status-infoBorder', accent: 'border-l-status-infoText', chip: 'bg-status-infoText/10 text-status-infoText', Icon: Info },
  emotional: { container: 'bg-emotional-bg border-emotional-border',   accent: 'border-l-emotional-text',  chip: 'bg-emotional-text/10 text-emotional-text',   Icon: Heart },
}

export function InfoBlock({ title, children, defaultOpen = false, variant = 'neutral' }: InfoBlockProps) {
  const [open, setOpen] = useState(defaultOpen)
  const s = STYLES[variant]
  const { Icon } = s

  return (
    <div className={cn('rounded-xl border border-l-4 overflow-hidden', s.container, s.accent)}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-black/[0.02]">
        <span className={cn('flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg', s.chip)}>
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="flex-1 text-sm font-semibold text-ink-2 leading-snug">{title}</span>
        {open
          ? <ChevronUp   className="h-4 w-4 flex-shrink-0 text-ink-4" aria-hidden="true" />
          : <ChevronDown className="h-4 w-4 flex-shrink-0 text-ink-4" aria-hidden="true" />}
      </button>
      {open && (
        <div className="px-4 pb-4 flex flex-col gap-2 text-[13px] leading-relaxed text-ink-3 border-t border-black/[0.06]">
          <div className="pt-3 flex flex-col gap-2">{children}</div>
        </div>
      )}
    </div>
  )
}
